import { DataType } from '../../../rpc/model/squareup/feature/relay/common/data_type';
import { GetFlagResponse } from 'rpc/model/squareup/feature/relay/flags/message/get_flags';

export enum AuthenticatedStringFlagKey {
  referralsExperimentVariant = 'referrals-individual-referrals-square-profiles-experiment',
}

type AuthenticatedStringFlagConfig = {
  flagKey: AuthenticatedStringFlagKey;
  defaultValue: string;
};

/*
 * These flags will automatically be loaded into the store after fetching the buyer.
 */
export const AuthenticatedStringFlags: AuthenticatedStringFlagConfig[] = [
  {
    defaultValue: 'ineligible',
    flagKey: AuthenticatedStringFlagKey.referralsExperimentVariant,
  },
];

/*
 * These flags will automatically be loaded into the store after fetching the buyer.
 */
export enum BoolFlag {
  useAppSubdomain = 'domainseparation-use-app-subdomain',
  useNewGiftCardFlow = 'giftcard-use-new-square-profile-flow',
  showCookieConsent = 'sq-profile-show-cookie-consent',
  showCardGroupReceiptSettings = 'buyerportal-receipt-settings-card-groups',
  showReceipts = 'buyerportal-should-show-square-profile-receipts',
  showPurchaseInsights = 'buyerportal-should-show-square-profile-purchase-insights',
  useNewMarketingPreferencesApi = 'buyerportal-use-new-marketing-preferences-api',
}

/*
 * These flags must be loaded into the store manually.
 */
export enum AnonymousBoolFlag {
  enableMerchantPortal = 'buyerportal-is-merchant-portal-available',
  manuallyTrackPageViews = 'crm-track-square-profile-views-manually',
  addReportReason = 'cus-buyerportal-fe-add-report-reason',
  enableReceiptLookupRoute = 'crm-receipt-lookup-square-profile-enabled',
  enableReceiptLookupOtp = 'crm-receipt-lookup-otp-enabled',
  marketMonochrome = 'uisystems-market-monochrome',
}

const isValidBoolFlag = (flagName: string): flagName is BoolFlag => {
  const isBoolFlag = (
    Object.keys(BoolFlag) as Array<keyof typeof BoolFlag>
  ).some((k) => BoolFlag[k] === flagName);

  const isAnonymousBoolFlag = (
    Object.keys(AnonymousBoolFlag) as Array<keyof typeof AnonymousBoolFlag>
  ).some((k) => AnonymousBoolFlag[k] === flagName);

  return isBoolFlag || isAnonymousBoolFlag;
};

const isValidAuthenticatedStringFlag = (
  flagName: string
): flagName is AuthenticatedStringFlagKey => {
  return Object.values(AuthenticatedStringFlagKey).includes(
    flagName as AuthenticatedStringFlagKey
  );
};

export type BoolFlagConfig = {
  [key in BoolFlag | AnonymousBoolFlag]: boolean;
};

export type StringFlagConfig = {
  [key in AuthenticatedStringFlagKey]: string;
};

export type Flags = {
  boolean: BoolFlagConfig;
  string: StringFlagConfig;
};

export type PartialFlags = {
  boolean: Partial<BoolFlagConfig>;
  string: Partial<StringFlagConfig>;
};

export const CreateDefaultBoolFlagConfig = (): BoolFlagConfig => {
  return [
    ...Object.values(BoolFlag),
    ...Object.values(AnonymousBoolFlag),
  ].reduce<BoolFlagConfig>((accum, curr) => {
    return {
      ...accum,
      [curr]: false,
    };
  }, {} as BoolFlagConfig);
};

export const CreateDefaultAuthenticatedStringFlagConfig = (): Record<
  AuthenticatedStringFlagKey,
  string
> => {
  return AuthenticatedStringFlags.reduce<
    Record<AuthenticatedStringFlagKey, string>
  >((accum, config) => {
    return {
      ...accum,
      [config.flagKey]: config.defaultValue,
    };
  }, {} as Record<AuthenticatedStringFlagKey, string>);
};

export const FlagConverter = {
  fromRpcGetFlagsResponse(response: readonly GetFlagResponse[]): PartialFlags {
    const flags: PartialFlags = {
      boolean: {},
      string: {},
    };

    response.forEach((flag) => {
      switch (flag.flagType) {
        case DataType.BOOLEAN: {
          if (isValidBoolFlag(flag.flagName)) {
            flags.boolean[flag.flagName] = flag.boolValue;
          }
          break;
        }
        case DataType.STRING: {
          if (isValidAuthenticatedStringFlag(flag.flagName)) {
            flags.string[flag.flagName] = flag.stringValue;
          }
          break;
        }
        default: {
          break;
        }
      }
    });

    return flags;
  },
};
